import { useEffect, useMemo, useState } from "react"
import { useAppDispatch, useAppSelector, useHeightResize } from "hooks"
import { AlertModal, AntdButton, ConfirmationModal, EmployeeSidebar } from "components"
import { selectSelectedActiveOrInactiveEmployeeDetails } from "store/Employee/detail.slice"
import EmployeeTable, { TableCell } from "./EmployeeTable"
import { addDateIfFinishTimeIsGreater, getMomentZInstanceOfDate } from "config/utils"
// import useSchedulesQueryParams from "hooks/useSchedulesQueryParams" // Replaced
//import useGlobalScheduleParams from "hooks/useGlobalScheduleParams"
import { useEmployeeScheduleDefaults } from "hooks/useScheduleDefaults"
import useSchedulesQueryParams from "hooks/useSchedulesQueryParams"
import {
	acceptSchedule,
	clearInactiveScheduleByEmployee,
	clearSchedule,
	deletingSchedule,
	getScheduleByEmployeeAndDateRange,
	saveInactiveSchedule,
	saveScheduleByEmployee,
	selectScheduleDelete,
	selectScheduleListByEmployee,
} from "store/Schedules/schedules.slice"
import moment from "moment"
import CreateEmployeeScheduleModal, { CreateScheduleByEmployeeFormInterface } from "./CreateEmployeeScheduleModal"
import { selectCurrentUser } from "store/auth.slice"
import { DATE_FORMATS, GLOBAL_PAYLOAD_DATE_FORMAT } from "config/constants"
import AllocateEmployeeModal from "./AllocateEmployeeModal"
import UpdateEmployeeScheduleModal from "./UpdateEmployeeScheduleModal"
import ScheduleCrudBar from "../ScheduleCrudBar"
import ScheduleAuditTrail from "../Location/ScheduleAuditTrailModal"
import CreateRecurringEmployeeScheduleModal from "./CreateRecurringEmployeeScheduleModal"
import { ScheduleStatus } from "../Constants/schedulesConstants"
import { DatePicker } from "antd"
import { ConfigProvider, Divider, Statistic } from "antd-v5"
import { deleteInactiveSchedule } from "store/Location/locationPosts.slice"
import { getLocationById, getLocationsByBranchId } from "store/Location/locationDetails.slice"

interface props {}

const Employee: React.FC<props> = () => {
	const dispatch = useAppDispatch()
	useHeightResize({ element: "employee-schedule-table" })

	useEmployeeScheduleDefaults() // Initialize Defaulting Logic

	const {
		date: paramDate,
		selectedEmployee: paramEmployee = "",
		selectedEmployeeBranch = "",
		setSearchParams,
	} = useSchedulesQueryParams()
	const [selectedCell, setSelectedCell] = useState<TableCell[]>([])
	const selectedDate = useMemo(() => {
		return paramDate
			? moment(paramDate, "YYYY-MM-DD").format(GLOBAL_PAYLOAD_DATE_FORMAT)
			: moment().format(GLOBAL_PAYLOAD_DATE_FORMAT)
	}, [paramDate])
	const [createOpen, setCreateOpen] = useState<boolean>(false)
	const [updateModalOpen, setUpdateModalOpen] = useState<boolean>(false)
	const [alertOpen, setAlertOpen] = useState<boolean>(false)
	const [deleteOpen, setDeleteOpen] = useState<boolean>(false)
	const [allocationModalOpen, setAllocationModalOpen] = useState<boolean>(false)
	const [openRecurringModal, setOpenRecurringModal] = useState<boolean>(false)
	const [showAuditScheduleModal, setShowAuditScheduleModal] = useState<boolean>(false)
	const [isRecurring, setIsRecurring] = useState<boolean>(false)
	const [createScheduleModalData, setCreateScheduleModalData] =
		useState<CreateScheduleByEmployeeFormInterface | null>(null)
	const [showDeleted, setShowDeleted] = useState<boolean>(false)
	const isScheduleDeleting = useAppSelector(selectScheduleDelete())
	const user = useAppSelector(selectCurrentUser())
	const selectedEmployee = useAppSelector(selectSelectedActiveOrInactiveEmployeeDetails(paramEmployee))

	const schedules = useAppSelector(
		selectScheduleListByEmployee(
			moment(selectedDate, DATE_FORMATS.YYYY_MM_DD).startOf("month").format(DATE_FORMATS.DD_MM_YYYY),
			showDeleted,
			paramEmployee,
		),
	)

	const scheduledHours = useMemo(() => {
		const result = schedules
			.filter(item => item.status !== ScheduleStatus.INACTIVE)
			.map(item => moment(item.finishTime).diff(moment(item.startTime), "hours"))
			.reduce((accumulator, currentValue) => accumulator + currentValue, 0)

		return result
	}, [schedules])

	const totalScheduledShiftsCount = useMemo(() => {
		return schedules.filter(item => item.status !== ScheduleStatus.INACTIVE).length
	}, [schedules])

	useEffect(() => {
		const dateForUrl = selectedDate ? moment(selectedDate, GLOBAL_PAYLOAD_DATE_FORMAT).format("YYYY-MM-DD") : ""

		if (dateForUrl && dateForUrl !== paramDate) {
			setSearchParams({ date: dateForUrl })
		}
	}, [selectedDate, paramDate])

	// useEffect(() => {
	// 	if (selectedEmployee?.branch) dispatch(setSelectedEmployeeBranch(selectedEmployee?.branch))
	// 	else if (selectedEmployee?.supplier) dispatch(selectedEmployee?.supplier))
	// }, [selectedEmployee])

	useEffect(() => {
		setSelectedCell([])
	}, [selectedEmployee, selectedDate])

	useEffect(() => {
		if (paramEmployee && selectedDate) {
			dispatch(
				getScheduleByEmployeeAndDateRange({
					startTime: moment(selectedDate).startOf("month").toISOString(),
					finishTime: moment(selectedDate).endOf("month").toISOString(),
					employee: paramEmployee,
				}),
			)
			dispatch(clearInactiveScheduleByEmployee())
		} else if (!paramEmployee) {
			dispatch(clearSchedule())
		}
	}, [selectedDate, paramEmployee])

	useEffect(() => {
		if (selectedCell?.length) {
			if (selectedCell[0]?.site) {
				dispatch(
					getLocationById(selectedCell[0]?.site._id, id => {
						if (id) setSearchParams({ location: id })
					}),
				)
			}
		}
	}, [selectedCell])

	const handleCreate = () => {
		if ((selectedCell?.length as number) === 1) {
			setCreateOpen(!createOpen)
		} else {
			setAlertOpen(!alertOpen)
		}
	}

	const handleDelete = (notes: string) => {
		if (selectedCell?.length >= 1) {
			const filterInactiveShifts = selectedCell?.filter(cell => cell.status === ScheduleStatus.INACTIVE)
			const filterActiveShifts = selectedCell?.filter(cell => cell.status !== ScheduleStatus.INACTIVE)

			if (filterActiveShifts.length) {
				const ids: string[] = []
				filterActiveShifts?.forEach(shift => {
					if (shift?._id) return ids.push(shift?._id)
				})
				if (ids.length) dispatch(deletingSchedule({ ids, notes }, () => setSelectedCell([])))
			}

			if (filterInactiveShifts.length) {
				let deleteInactiveShiftPayload: { [key: string]: { _id: string; until: Date }[] } = {}

				filterInactiveShifts.forEach(shift => {
					const postId = typeof shift.post === "string" ? shift.post : shift.post._id

					const payload = {
						_id: shift._id ?? "",
						until: getMomentZInstanceOfDate(shift?.startTime)
							.clone()
							.subtract(1, shift.isFourOnFourOff ? "d" : "w")
							.endOf("day")
							.utc()
							.toDate(),
					}

					deleteInactiveShiftPayload = {
						...deleteInactiveShiftPayload,
						[postId]: deleteInactiveShiftPayload[postId]
							? [...deleteInactiveShiftPayload[postId], payload]
							: [payload],
					}
				})

				if (Object.keys(deleteInactiveShiftPayload).length) {
					dispatch(deleteInactiveSchedule(deleteInactiveShiftPayload, () => setSelectedCell([])))
				}
			}
		} else {
			setAlertOpen(!alertOpen)
		}
	}

	const cellSelectionHandler = (e: any) => {
		if (e) setSelectedCell([e])
		else setSelectedCell(() => [])
	}

	const onAcceptSchedule = () => {
		const payload = selectedCell?.map(schedule => schedule?._id || "") || []
		dispatch(acceptSchedule(payload, () => setSelectedCell([])))
	}

	const disableButtons = useMemo(() => {
		return selectedCell?.some(cell => !cell._id)
	}, [selectedCell])

	const handlePrev = () => {
		const newDate = moment(selectedDate, GLOBAL_PAYLOAD_DATE_FORMAT).subtract(1, "month").format("YYYY-MM-DD")
		setSearchParams({ date: newDate })
		setSelectedCell([])
	}
	const handleNext = () => {
		const newDate = moment(selectedDate, GLOBAL_PAYLOAD_DATE_FORMAT).add(1, "month").format("YYYY-MM-DD")
		setSearchParams({ date: newDate })
		setSelectedCell([])
	}

	const isInactiveScheduleSelected = useMemo(
		() => !!selectedCell.find(cell => cell.status === ScheduleStatus.INACTIVE) || false,
		[selectedCell],
	)

	const isScheduleToEmployeeOnlyEventSelected = useMemo(
		() => !!selectedCell.find(cell => cell.eventType?.scheduledToEmployee && !cell.eventType?.scheduledToPost),
		[selectedCell],
	)

	const onRecurringSchedules = () => {
		if (isInactiveScheduleSelected) {
			setOpenRecurringModal(true)
		} else {
			setCreateOpen(true)
			setIsRecurring(true)
		}
	}

	const handleClearCells = () => {
		setCreateScheduleModalData(null)
		setSelectedCell([])
	}

	const resetTable = () => {
		setCreateScheduleModalData(null)
		setSelectedCell([])
	}

	const onSubmit = (data: CreateScheduleByEmployeeFormInterface) => {
		const {
			startTime,
			finishTime,
			eventType,
			notes,
			bypassSiaVerification,
			bypassMaxConsecutiveWorkdaysVerification,
		} = data

		// const starTimeMoment = !startTime
		// 	? moment(selectedCell?.[0].date, DATE_FORMATS.DD_MM_YYYY).startOf("day").toISOString()
		// 	: convertDateTimeStringToIsoUtc(`${selectedCell?.[0].date} ${startTime}`)
		// let finishTimeMoment = !finishTime
		// 	? moment(selectedCell?.[0].date, DATE_FORMATS.DD_MM_YYYY).endOf("day").toISOString()
		// 	: convertDateTimeStringToIsoUtc(`${selectedCell?.[0].date} ${finishTime}`)

		// finishTimeMoment = AddDateIfFinishTimeIsGreater(starTimeMoment, finishTimeMoment)

		const formattedStartTime = !startTime
			? moment(selectedCell?.[0].date, DATE_FORMATS.DD_MM_YYYY).startOf("day").format(DATE_FORMATS.HH_mm)
			: startTime

		const formattedFinishTime = !finishTime
			? moment(selectedCell?.[0].date, DATE_FORMATS.DD_MM_YYYY).endOf("day").format(DATE_FORMATS.HH_mm)
			: finishTime

		const { utcStartTime, utcFinishTime } = addDateIfFinishTimeIsGreater(
			formattedStartTime,
			formattedFinishTime,
			selectedCell?.[0].date,
		)

		if (isRecurring) {
			setCreateScheduleModalData({
				...data,
				date: selectedCell?.[0].date,
				startTime: utcStartTime,
				finishTime: utcFinishTime,
				employee: paramEmployee,
			})
			setOpenRecurringModal(true)
			setCreateOpen(false)
			return
		}

		const payload = {
			location: data.location,
			employee: paramEmployee,
			contract: data.contract,
			user: user?._id,
			posts: [{ postId: data.post, dates: [{ startTime: utcStartTime, finishTime: utcFinishTime }] }],
			bypassSiaVerification,
			bypassMaxConsecutiveWorkdaysVerification,
			eventType,
			notes,
		}
		dispatch(
			saveScheduleByEmployee(payload, () => {
				setSelectedCell([])
				setCreateOpen(false)
			}),
		)
	}

	const onCreateScheduleFromInactiveSchedule = () => {
		const payload = selectedCell.map(data => {
			return {
				...(data?.employee && { employee: data?.employee }),
				location: (data as any)?.["location"]?._id || "", // TODO: needs to be refactored
				posts: [
					{
						// TODO: temporary fix
						postId: (data?.post as any)?.["_id"] || "",
						dates: [
							{
								startTime: data?.startTime,
								finishTime: data?.finishTime,
							},
						],
					},
				],
				eventType: (data?.eventType as any)?.["_id"] || "",
				user: user?._id,
				createdFromRecur: data?._id,
			}
		})

		dispatch(saveInactiveSchedule(payload, () => resetTable()))
	}

	useEffect(() => {
		if (selectedEmployeeBranch) dispatch(getLocationsByBranchId(selectedEmployeeBranch, true))
	}, [selectedEmployeeBranch])

	return (
		<>
			<EmployeeSidebar />
			<ScheduleCrudBar
				onCreateSchedules={handleCreate}
				onRecurringSchedules={onRecurringSchedules}
				onAcceptSchedule={onAcceptSchedule}
				onUpdateSchedules={() => setUpdateModalOpen(true)}
				onAllocateSchedule={() => setAllocationModalOpen(true)}
				onCreateInActiveSchedule={onCreateScheduleFromInactiveSchedule}
				onIncidentReport={() => ""}
				onDeleteSchedule={() => setDeleteOpen(true)}
				onAuditSchedule={() => setShowAuditScheduleModal(true)}
				showDeletedSchedules={val => setShowDeleted(val)}
				disableCreate={!selectedCell?.length}
				disableAccept={disableButtons || isInactiveScheduleSelected || isScheduleToEmployeeOnlyEventSelected}
				disableUpdate={disableButtons || isInactiveScheduleSelected}
				disableAllocate={disableButtons || isInactiveScheduleSelected || isScheduleToEmployeeOnlyEventSelected}
				disableIncident={true}
				disableAuto={!isInactiveScheduleSelected}
				disableAudit={selectedCell?.[0]?._id || ""}
				disableDelete={disableButtons}
			/>
			<div className="flex flex-col gap-2.5 py-2.5">
				<ConfigProvider
					theme={{
						components: {
							Divider: {
								marginLG: 0,
							},
						},
					}}
				>
					<Divider />
					<div className="flex items-center justify-between">
						<div className="flex gap-4">
							<Statistic
								key="Employee"
								title={<span className="font-semibold 3xl:text-md">Employee</span>}
								value={
									selectedEmployee
										? `${selectedEmployee.firstName} ${selectedEmployee.lastName}`
										: "No employee"
								}
								formatter={val => (
									<span className="font-semibold text-gray-800 3xl:text-md">{val}</span>
								)}
							/>
							<Statistic
								key="ScheduledHours"
								title={<span className="font-semibold 3xl:text-md">Scheduled Hours</span>}
								value={scheduledHours || 0}
								formatter={val => (
									<span className="font-semibold text-gray-800 3xl:text-md">{val}</span>
								)}
							/>
							<Statistic
								key="ScheduledShifts"
								title={<span className="font-semibold 3xl:text-md">Scheduled Shifts</span>}
								value={totalScheduledShiftsCount}
								formatter={val => (
									<span className="font-semibold text-gray-800 3xl:text-md">{val}</span>
								)}
							/>
						</div>
						<div className="flex flex-row space-x-1">
							<DatePicker
								value={moment(selectedDate, GLOBAL_PAYLOAD_DATE_FORMAT)}
								format={GLOBAL_PAYLOAD_DATE_FORMAT}
								placeholder={DATE_FORMATS.DD_MM_YYYY}
								onChange={date => {
									setSearchParams({
										date: moment(date, GLOBAL_PAYLOAD_DATE_FORMAT).format("YYYY-MM-DD"),
									})
								}}
							/>

							<AntdButton onClick={handlePrev}>Prev</AntdButton>
							<AntdButton onClick={handleNext}>Next</AntdButton>
							<AntdButton onClick={handleClearCells}>Clear</AntdButton>
						</div>
					</div>
					<Divider />
				</ConfigProvider>

				<div id="employee-schedule-table" className="schedule-scrollbar overflow-y-auto">
					<EmployeeTable
						showDeleted={showDeleted}
						date={selectedDate}
						selectedCell={selectedCell}
						cellSelectionHandler={cellSelectionHandler}
						selectedEmployee={paramEmployee}
					/>
				</div>
			</div>
			{createOpen && (
				<CreateEmployeeScheduleModal
					onSubmit={onSubmit}
					open={createOpen}
					modalHandler={close => {
						setCreateOpen(close)
						setIsRecurring(false)
					}}
					isRecurringSchedule={isRecurring}
				/>
			)}
			{openRecurringModal && (
				<CreateRecurringEmployeeScheduleModal
					open={openRecurringModal}
					modalHandler={val => {
						setOpenRecurringModal(val)
						setCreateScheduleModalData(null)
						setIsRecurring(false)
					}}
					createScheduleModalData={createScheduleModalData}
					selectedCell={selectedCell}
					resetTable={resetTable}
				/>
			)}
			{alertOpen && (
				<AlertModal
					open={alertOpen}
					message="Please select one Cell to Create schedule for Employee"
					modalHandler={close => {
						setAlertOpen(close)
					}}
				/>
			)}
			{deleteOpen && (
				<ConfirmationModal
					confirmationAction={false}
					open={deleteOpen}
					loader={isScheduleDeleting}
					confirmationText={"Delete"}
					modalHandler={close => {
						setDeleteOpen(close)
						setSelectedCell([])
					}}
					onConfirmation={notes => {
						handleDelete(notes)
						setDeleteOpen(false)
					}}
				/>
			)}
			{allocationModalOpen && (
				<AllocateEmployeeModal
					open={allocationModalOpen}
					setSelectedCell={setSelectedCell}
					modalHandler={setAllocationModalOpen}
					selectedSchedules={selectedCell || []}
				/>
			)}
			{updateModalOpen && (
				<UpdateEmployeeScheduleModal
					open={updateModalOpen}
					setSelectedCell={setSelectedCell}
					modalHandler={setUpdateModalOpen}
					selectedSchedules={selectedCell || []}
				/>
			)}
			<ScheduleAuditTrail
				modalHandler={setShowAuditScheduleModal}
				open={showAuditScheduleModal}
				scheduleId={selectedCell[0]?._id || ""}
			/>
		</>
	)
}

export default Employee
